import { FlexibleModuleEnum, ModuleColumnEnum } from 'generated/graphql';
import type { PageBuilderModuleType } from '~/modules/page-builder/types';
import SingleImageModule from '~/modules/page-builder/component/detail-article/single-image-module/SingleImageModule';
import DuoParagraphModule from '../duo-paragraph-module/DuoParagraphModule';
import DuoImageModule from '../duo-image-module/DuoImageModule';
import SingleParagraphModule from '../single-paragraph-module/SingleParagraphModule';
import ImageParagraphModule from '../image-paragraph-module/ImageParagraphModule';
import GalleryModule from '../gallery-module/GalleryModule';
import SingleVideoModule from '../single-video-module/SingleVideoModule';
import VideoParagraphModule from '../video-paragraph-module/VideoParagraphModule';
import QuoteModule from '../quote-module/QuoteModule';
import { ProposalSection } from '~/modules/ecommerce/pages/ProposalSection';
import { mapInvoiceModuleToProposal } from '~/modules/ecommerce/util/invoice-module-to-proposal-mapper';
import type { InvoiceColumnProps } from '~/modules/page-builder/component/edit/invoice-module/types';

type ViewMode = 'normal' | 'preview' | 'pdf';

interface DetailModuleProps {
    module: PageBuilderModuleType;
    autoPlayVideoModuleId?: string;
    /** View mode for proposal modules: 'normal', 'preview', or 'pdf' */
    viewMode?: ViewMode;
}

/**
 * A rendered Page Builder module on the website. The module component rendered
 * depends on the module type.
 */
const DetailModule = ({
    module,
    autoPlayVideoModuleId,
    viewMode = 'normal',
}: DetailModuleProps) => {
    const columns = module.columns;
    const meta = module.meta;

    const firstColumn = columns[0];
    if (!firstColumn) {
        return null;
    }

    switch (module.presetType) {
        case FlexibleModuleEnum.InvoiceModule:
            if (
                firstColumn.__typename === 'Invoice' ||
                firstColumn.__typename === 'QuoteTemplate'
            ) {
                const professionalData =
                    meta?.professional?.id && meta?.professional?.title
                        ? {
                              id: meta.professional.id,
                              title: meta.professional.title,
                          }
                        : undefined;
                const proposal = mapInvoiceModuleToProposal(
                    firstColumn as InvoiceColumnProps,
                    meta?.parentUrl || '',
                    professionalData,
                    meta?.invoiceableSignatureFallback,
                );
                return (
                    <ProposalSection proposal={proposal} viewMode={viewMode} />
                );
            }
            return null;
        case FlexibleModuleEnum.ImageParagraphModule:
            return <ImageParagraphModule columns={columns} meta={meta} />;
        case FlexibleModuleEnum.VideoParagraphModule:
            return <VideoParagraphModule columns={columns} meta={meta} />;
        case FlexibleModuleEnum.SingleVideoModule:
            return (
                <SingleVideoModule
                    column={firstColumn}
                    shouldAutoPlayVideo={autoPlayVideoModuleId === module.id}
                    isCoverGrayscale={module.isCoverGrayscale}
                />
            );
        case FlexibleModuleEnum.HeadingModule:
        case FlexibleModuleEnum.SingleParagraphModule:
            return <SingleParagraphModule column={firstColumn} meta={meta} />;
        case FlexibleModuleEnum.DuoParagraphModule:
            return <DuoParagraphModule columns={columns} meta={meta} />;
        // The revamp's editorial text modules have no layout here. Readers on
        // the legacy article still get the copy, stacked in column order,
        // rather than a blank section where the module should be.
        case FlexibleModuleEnum.SloganModule:
        case FlexibleModuleEnum.FullWidthSloganModule:
        case FlexibleModuleEnum.SloganParagraphModule:
        case FlexibleModuleEnum.QuadParagraphModule: {
            const textColumns = columns.filter((column) => column.contentHTML);
            if (!textColumns.length) {
                return null;
            }
            return (
                <div className="flex w-full flex-col gap-7">
                    {textColumns.map((column) => (
                        <SingleParagraphModule
                            key={column.id}
                            column={column}
                            meta={meta}
                        />
                    ))}
                </div>
            );
        }
        case FlexibleModuleEnum.QuoteModule:
            return <QuoteModule column={firstColumn} meta={meta} />;
        case FlexibleModuleEnum.SingleImageModule:
            return <SingleImageModule column={firstColumn} />;
        case FlexibleModuleEnum.DuoImageModule:
            return <DuoImageModule columns={columns} />;
        // A gallery preset can carry the revamp's optional h3 title column, and
        // only the revamp render has a heading for it. Here every column the
        // gallery is given becomes a tile, so the title would take a slot in
        // the grid as a blank one. The article editor splits it out the same
        // way, and drops it when the revamp gate is off.
        case FlexibleModuleEnum.QuadImageModule:
        case FlexibleModuleEnum.OctaImageModule:
            return (
                <GalleryModule
                    columns={columns.filter(
                        (column) => column.type !== ModuleColumnEnum.TextColumn,
                    )}
                />
            );
        default:
            return null;
    }
};

export default DetailModule;
